import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Candy, Search, Heart, ShoppingCart, X, Grid2x2, Flame } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useDevice } from "@/components/Device";
import { CategoryIcon } from "@/components/CategoryIcon";

export function MobileBottomNav() {
  const device = useDevice();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { cartCount, cartSubtotal, setCartOpen, favorites } = useApp();
  const [catsOpen, setCatsOpen] = useState(false);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories?visible_only=true").then(({ data }) => setCats(data.filter((x) => !x.parent_id))).catch(() => {});
  }, []);

  // Close the overlay whenever the route changes.
  useEffect(() => { setCatsOpen(false); }, [pathname]);

  if (device !== "mobile" || pathname.startsWith("/admin")) return null;

  const focusSearch = () => { window.scrollTo({ top: 0, behavior: "smooth" }); setTimeout(() => document.querySelector('[data-testid="search-input"]')?.focus(), 350); };
  const go = (path) => { setCatsOpen(false); navigate(path); };
  const item = "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold";
  const act = (p) => (pathname === p ? "text-primary" : "text-muted-foreground");

  return (
    <>
      <AnimatePresence>
        {catsOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setCatsOpen(false)}
            data-testid="mobile-categories-overlay"
            className="fixed inset-0 z-[60] flex flex-col bg-black/55 backdrop-blur-md pb-[calc(env(safe-area-inset-bottom)+68px)]"
          >
            <div className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)] pb-3">
              <p className="text-lg font-extrabold tracking-tight text-white">Kategorier</p>
              <button onClick={() => setCatsOpen(false)} data-testid="mobile-categories-close" aria-label="Luk" className="rounded-full bg-white/15 p-2 text-white backdrop-blur-sm transition-transform active:scale-95">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6" onClick={(e) => e.stopPropagation()}>
              <div className="grid grid-cols-2 gap-3">
                <motion.button
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.02 }}
                  onClick={() => go("/produkter")} data-testid="mobile-cat-all"
                  className="col-span-2 flex items-center gap-3 rounded-2xl border border-white/15 bg-white/95 p-4 text-left shadow-lg transition-transform active:scale-[0.98]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><Grid2x2 className="h-6 w-6 text-primary" /></span>
                  <span className="text-base font-bold tracking-tight">Alle produkter</span>
                </motion.button>

                {cats.map((c, i) => (
                  <motion.button
                    key={c.id}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 + i * 0.03 }}
                    onClick={() => go(`/kategori/${c.slug}`)} data-testid={`mobile-cat-${c.slug}`}
                    className="flex min-h-[112px] flex-col items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/95 p-4 text-center shadow-lg transition-transform active:scale-[0.97]"
                  >
                    <CategoryIcon cat={c} size={44} />
                    <span className="text-sm font-bold leading-tight tracking-tight">{c.name}</span>
                  </motion.button>
                ))}

                <motion.button
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 + cats.length * 0.03 }}
                  onClick={() => go("/tilbud")} data-testid="mobile-cat-offers"
                  className="col-span-2 flex items-center gap-3 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/90 to-primary p-4 text-left text-primary-foreground shadow-lg transition-transform active:scale-[0.98]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20"><Flame className="h-6 w-6" /></span>
                  <span className="text-base font-bold tracking-tight">Aktuelle tilbud</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <nav className="fixed inset-x-0 bottom-0 z-[56] border-t border-border bg-background/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        <div className="flex items-stretch">
          <Link to="/" className={`${item} ${act("/")}`} data-testid="bottom-nav-home"><Home className="h-5 w-5" />Forside</Link>
          <button onClick={() => setCatsOpen((v) => !v)} className={`${item} ${catsOpen ? "text-primary" : "text-muted-foreground"}`} data-testid="bottom-nav-categories"><Candy className="h-5 w-5" />Kategorier</button>
          <button onClick={focusSearch} className={`${item} text-muted-foreground`} data-testid="bottom-nav-search"><Search className="h-5 w-5" />Søg</button>
          <Link to="/favoritter" className={`${item} relative ${act("/favoritter")}`} data-testid="bottom-nav-favorites">
            <Heart className="h-5 w-5" />Favoritter
            {favorites.length > 0 && <span className="absolute right-3 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">{favorites.length}</span>}
          </Link>
          <button onClick={() => setCartOpen(true)} className={`${item} relative text-primary`} data-testid="bottom-nav-cart">
            <ShoppingCart className="h-5 w-5" />
            <span>{cartCount > 0 ? kr(cartSubtotal) : "Kurv"}</span>
            {cartCount > 0 && <span className="absolute right-3 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground" data-testid="bottom-nav-cart-count">{cartCount}</span>}
          </button>
        </div>
      </nav>
    </>
  );
}
